import { useState, useEffect } from 'react';
import {
  GitBranch,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  GripVertical,
  Users,
  Shield,
  Truck,
  Clock,
  X,
} from 'lucide-react';
import api from '../services/api';
import { WorkflowTemplate, WorkflowTemplateStage, Department } from '../types';
import Layout from '../components/layout/Layout';

export default function WorkflowTemplates() {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkflowTemplate | null>(null);
  const [editingStage, setEditingStage] = useState<WorkflowTemplateStage | null>(null);
  const [addingStageToTemplate, setAddingStageToTemplate] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, departmentsRes] = await Promise.all([
        api.get('/workflows/templates?include_inactive=true'),
        api.get('/departments').catch(() => ({ data: { success: true, data: [] } })),
      ]);

      if (templatesRes.data.success) {
        setTemplates(templatesRes.data.data);
      }
      if (departmentsRes.data.success) {
        setDepartments(departmentsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateDetails = async (templateId: number) => {
    try {
      const response = await api.get(`/workflows/templates/${templateId}`);
      if (response.data.success) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === templateId ? response.data.data : t))
        );
      }
    } catch (error) {
      console.error('Failed to fetch template details:', error);
    }
  };

  const handleToggleTemplate = async (templateId: number) => {
    if (expandedTemplate === templateId) {
      setExpandedTemplate(null);
    } else {
      setExpandedTemplate(templateId);
      // Fetch full template details including stages
      await fetchTemplateDetails(templateId);
    }
  };

  const handleCreateTemplate = async (data: Partial<WorkflowTemplate>) => {
    try {
      await api.post('/workflows/templates', data);
      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      console.error('Failed to create template:', error);
    }
  };

  const handleUpdateTemplate = async (id: number, data: Partial<WorkflowTemplate>) => {
    try {
      await api.put(`/workflows/templates/${id}`, data);
      setEditingTemplate(null);
      await fetchTemplateDetails(id);
    } catch (error) {
      console.error('Failed to update template:', error);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/workflows/templates/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleAddStage = async (templateId: number, data: Partial<WorkflowTemplateStage>) => {
    try {
      await api.post(`/workflows/templates/${templateId}/stages`, data);
      setAddingStageToTemplate(null);
      await fetchTemplateDetails(templateId);
    } catch (error) {
      console.error('Failed to add stage:', error);
    }
  };

  const handleUpdateStage = async (stageId: number, data: Partial<WorkflowTemplateStage>) => {
    try {
      await api.put(`/workflows/templates/stages/${stageId}`, data);
      setEditingStage(null);
      if (expandedTemplate) {
        await fetchTemplateDetails(expandedTemplate);
      }
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  const handleDeleteStage = async (stageId: number) => {
    if (!confirm('Are you sure you want to delete this stage?')) return;
    try {
      await api.delete(`/workflows/templates/stages/${stageId}`);
      if (expandedTemplate) {
        await fetchTemplateDetails(expandedTemplate);
      }
    } catch (error) {
      console.error('Failed to delete stage:', error);
    }
  };

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <GitBranch className="text-primary-500" size={28} />
            <h1 className="text-2xl font-semibold">Workflow Templates</h1>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary flex items-center gap-2">
            <Plus size={20} />
            New Template
          </button>
        </div>

        {/* Search */}
        <div className="card mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" size={20} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="input pl-10 w-full"
            />
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="card text-center py-12">
            <GitBranch size={48} className="mx-auto mb-4 opacity-50 text-dark-muted" />
            <p className="text-dark-muted">No workflow templates found</p>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary mt-4">
              Create First Template
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTemplates.map((template) => (
              <div key={template.id} className="card">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => handleToggleTemplate(template.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold">{template.name}</h3>
                      {template.is_default && (
                        <span className="text-xs bg-primary-500/20 text-primary-500 px-2 py-0.5 rounded">Default</span>
                      )}
                      {!template.is_active && (
                        <span className="text-xs bg-dark-muted/20 text-dark-muted px-2 py-0.5 rounded">Inactive</span>
                      )}
                      {template.requires_approval && (
                        <span className="text-xs bg-status-yellow/20 text-status-yellow px-2 py-0.5 rounded flex items-center gap-1">
                          <Shield size={10} />
                          Requires Approval
                        </span>
                      )}
                      {template.ticket_type && (
                        <span className="text-xs bg-dark-elevated px-2 py-0.5 rounded">{template.ticket_type}</span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-dark-muted mt-1">{template.description}</p>
                    )}
                    <p className="text-xs text-dark-muted mt-2">
                      {template.stages?.length || 0} stages
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTemplate(template);
                      }}
                      className="p-2 hover:bg-dark-elevated rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      className="p-2 hover:bg-dark-elevated rounded-lg transition-colors text-status-red"
                    >
                      <Trash2 size={16} />
                    </button>
                    {expandedTemplate === template.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Expanded Stages */}
                {expandedTemplate === template.id && (
                  <div className="mt-4 pt-4 border-t border-dark-border">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Stages</h4>
                      <button
                        onClick={() => setAddingStageToTemplate(template.id)}
                        className="btn btn-secondary btn-sm"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Stage
                      </button>
                    </div>

                    {template.stages && template.stages.length > 0 ? (
                      <div className="space-y-2">
                        {template.stages.map((stage, index) => (
                          <div
                            key={stage.id}
                            className="flex items-center gap-3 p-3 bg-dark-elevated rounded-lg"
                          >
                            <GripVertical size={16} className="text-dark-muted cursor-grab" />
                            <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 text-sm font-medium">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{stage.name}</span>
                                {!stage.is_required && (
                                  <span className="text-xs bg-dark-muted/20 text-dark-muted px-1.5 py-0.5 rounded">
                                    Optional
                                  </span>
                                )}
                                {stage.requires_approval && (
                                  <span title="Requires Approval">
                                    <Shield size={12} className="text-status-yellow" />
                                  </span>
                                )}
                                {stage.auto_create_delivery_note && (
                                  <span title="Auto-creates Delivery Note">
                                    <Truck size={12} className="text-primary-500" />
                                  </span>
                                )}
                                {stage.can_run_parallel && (
                                  <span className="text-xs bg-primary-500/20 text-primary-500 px-1.5 py-0.5 rounded">
                                    Parallel {stage.parallel_group}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-dark-muted mt-1">
                                {stage.department_name && (
                                  <span className="flex items-center gap-1">
                                    <Users size={10} />
                                    {stage.department_name}
                                  </span>
                                )}
                                {stage.estimated_hours && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={10} />
                                    {stage.estimated_hours}h
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingStage(stage)}
                                className="p-1.5 hover:bg-dark-border rounded transition-colors"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteStage(stage.id)}
                                className="p-1.5 hover:bg-dark-border rounded transition-colors text-status-red"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-dark-muted">
                        <p>No stages defined</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Template Modal */}
        {showCreateModal && (
          <TemplateModal
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateTemplate}
          />
        )}

        {/* Edit Template Modal */}
        {editingTemplate && (
          <TemplateModal
            template={editingTemplate}
            onClose={() => setEditingTemplate(null)}
            onSave={(data) => handleUpdateTemplate(editingTemplate.id, data)}
          />
        )}

        {/* Add Stage Modal */}
        {addingStageToTemplate && (
          <StageModal
            departments={departments}
            onClose={() => setAddingStageToTemplate(null)}
            onSave={(data) => handleAddStage(addingStageToTemplate, data)}
          />
        )}

        {/* Edit Stage Modal */}
        {editingStage && (
          <StageModal
            stage={editingStage}
            departments={departments}
            onClose={() => setEditingStage(null)}
            onSave={(data) => handleUpdateStage(editingStage.id, data)}
          />
        )}
      </div>
    </Layout>
  );
}

// Template Modal Component
function TemplateModal({
  template,
  onClose,
  onSave,
}: {
  template?: WorkflowTemplate;
  onClose: () => void;
  onSave: (data: Partial<WorkflowTemplate>) => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [ticketType, setTicketType] = useState(template?.ticket_type || '');
  const [isDefault, setIsDefault] = useState(template?.is_default || false);
  const [requiresApproval, setRequiresApproval] = useState(template?.requires_approval || false);
  const [isActive, setIsActive] = useState(template?.is_active ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      description: description || undefined,
      ticket_type: ticketType || undefined,
      is_default: isDefault,
      requires_approval: requiresApproval,
      is_active: isActive,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-surface rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{template ? 'Edit Template' : 'Create Template'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-dark-elevated rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-dark-muted mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="input w-full"
              placeholder="e.g., Standard Installation"
            />
          </div>

          <div>
            <label className="block text-sm text-dark-muted mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full resize-none"
              rows={3}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm text-dark-muted mb-1">Job Type</label>
            <select
              value={ticketType}
              onChange={(e) => setTicketType(e.target.value)}
              className="input w-full"
            >
              <option value="">All Types</option>
              <option value="INSTALLATION">Installation</option>
              <option value="REPAIR">Repair</option>
              <option value="SUPPORT">Support</option>
              <option value="CONTENT">Content</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Set as default template</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresApproval}
                onChange={(e) => setRequiresApproval(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Require approval for stages</span>
            </label>

            {template && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm">Active</span>
              </label>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={!name} className="btn btn-primary">
              {template ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Stage Modal Component
function StageModal({
  stage,
  departments,
  onClose,
  onSave,
}: {
  stage?: WorkflowTemplateStage;
  departments: Department[];
  onClose: () => void;
  onSave: (data: Partial<WorkflowTemplateStage>) => void;
}) {
  const [name, setName] = useState(stage?.name || '');
  const [description, setDescription] = useState(stage?.description || '');
  const [departmentId, setDepartmentId] = useState<number | undefined>(stage?.department_id);
  const [estimatedHours, setEstimatedHours] = useState<number | undefined>(stage?.estimated_hours);
  const [isRequired, setIsRequired] = useState(stage?.is_required ?? true);
  const [requiresApproval, setRequiresApproval] = useState(stage?.requires_approval || false);
  const [canRunParallel, setCanRunParallel] = useState(stage?.can_run_parallel || false);
  const [parallelGroup, setParallelGroup] = useState<number | undefined>(stage?.parallel_group);
  const [autoCreateDeliveryNote, setAutoCreateDeliveryNote] = useState(stage?.auto_create_delivery_note || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      description: description || undefined,
      department_id: departmentId,
      estimated_hours: estimatedHours,
      is_required: isRequired,
      requires_approval: requiresApproval,
      can_run_parallel: canRunParallel,
      parallel_group: canRunParallel ? parallelGroup : undefined,
      auto_create_delivery_note: autoCreateDeliveryNote,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-surface rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{stage ? 'Edit Stage' : 'Add Stage'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-dark-elevated rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-dark-muted mb-1">Stage Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="input w-full"
              placeholder="e.g., Stock Picking"
            />
          </div>

          <div>
            <label className="block text-sm text-dark-muted mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full resize-none"
              rows={2}
              placeholder="Instructions for this stage..."
            />
          </div>

          <div>
            <label className="block text-sm text-dark-muted mb-1">Department</label>
            <select
              value={departmentId || ''}
              onChange={(e) => setDepartmentId(e.target.value ? parseInt(e.target.value) : undefined)}
              className="input w-full"
            >
              <option value="">Any Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-dark-muted mt-1">Only users from this department can complete the stage</p>
          </div>

          <div>
            <label className="block text-sm text-dark-muted mb-1">Estimated Hours</label>
            <input
              type="number"
              value={estimatedHours || ''}
              onChange={(e) => setEstimatedHours(e.target.value ? parseFloat(e.target.value) : undefined)}
              className="input w-full"
              placeholder="e.g., 2.5"
              min="0"
              step="0.5"
            />
          </div>

          <div className="space-y-3 pt-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Required stage</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresApproval}
                onChange={(e) => setRequiresApproval(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm">Requires approval</span>
                <p className="text-xs text-dark-muted">Manager must approve before stage is completed</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={canRunParallel}
                onChange={(e) => setCanRunParallel(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm">Can run in parallel</span>
                <p className="text-xs text-dark-muted">This stage can run alongside other parallel stages</p>
              </div>
            </label>

            {canRunParallel && (
              <div className="ml-7">
                <label className="block text-sm text-dark-muted mb-1">Parallel Group</label>
                <input
                  type="number"
                  value={parallelGroup || ''}
                  onChange={(e) => setParallelGroup(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="input w-full"
                  placeholder="e.g., 1"
                  min="1"
                />
                <p className="text-xs text-dark-muted mt-1">Stages in the same group run together</p>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoCreateDeliveryNote}
                onChange={(e) => setAutoCreateDeliveryNote(e.target.checked)}
                className="w-4 h-4 rounded border-dark-border text-primary-500 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm">Auto-create delivery note</span>
                <p className="text-xs text-dark-muted">Create a delivery note when this stage completes</p>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={!name} className="btn btn-primary">
              {stage ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
